import { useEffect, useState } from 'react'

import type {
  UsersAccessTechnicianFormErrors,
  UsersAccessTechnicianFormValuesI,
} from '../types/usersAccess'
import { useUsersAccessFacilityOptions } from './useUsersAccessFacilityOptions'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { useTranslation } from 'react-i18next'

import * as EmailValidator from 'email-validator'

import type { ColorScheme } from '@hivewatch/dark'

import {
  TechniciansDocument,
  useCreateTechnicianMutation,
} from '@/graphql/generated/hooks'

const INITIAL_VALUES: UsersAccessTechnicianFormValuesI = {
  deviceTypes: [],
  email: '',
  facilityIds: [],
  firstName: '',
  lastName: '',
}

export const useUsersAccessCreateTechnician = (colorScheme: ColorScheme) => {
  const { t } = useTranslation()
  const { shouldEnableCreateTechnician } = usePermissions()
  const { showError, showSuccess } = useToasts({
    colorScheme,
    isRebrand: true,
  })
  const [values, setValues] =
    useState<UsersAccessTechnicianFormValuesI>(INITIAL_VALUES)
  const [errors, setErrors] = useState<UsersAccessTechnicianFormErrors>({})
  const {
    isFacilitiesError,
    facilityOptions,
    isFacilitiesLoading,
    setFacilitySearch,
  } = useUsersAccessFacilityOptions()
  const [createTechnician, { loading: isCreating }] =
    useCreateTechnicianMutation()

  useEffect(() => {
    if (!isFacilitiesError) return
    // useFacilitySearch only surfaces a boolean — the underlying error is
    // already captured there with generic search tags; this adds the
    // surface-specific context worth triaging on separately.
    Sentry.captureMessage('Failed to load facilities for technician create', {
      level: 'error',
      tags: {
        action: 'load_create_technician',
        feature: 'managed_accounts',
        surface: 'settings_users_access',
      },
    })
  }, [isFacilitiesError])

  const setField = <FieldT extends keyof UsersAccessTechnicianFormValuesI>(
    field: FieldT,
    value: UsersAccessTechnicianFormValuesI[FieldT]
  ) => {
    setValues((current) => ({ ...current, [field]: value }))
    setErrors((current) => ({ ...current, [field]: undefined }))
  }

  const validate = () => {
    const nextErrors: UsersAccessTechnicianFormErrors = {}
    if (!values.firstName.trim()) {
      nextErrors.firstName = t('settings:rebrand_users_first_name_required')
    }
    if (!values.lastName.trim()) {
      nextErrors.lastName = t('settings:rebrand_users_last_name_required')
    }
    if (!EmailValidator.validate(values.email.trim())) {
      nextErrors.email = t('settings:rebrand_users_email_invalid')
    }
    if (values.facilityIds.length === 0) {
      nextErrors.facilityIds = t('settings:rebrand_users_facilities_required')
    }
    if (values.deviceTypes.length === 0) {
      nextErrors.deviceTypes = t('settings:rebrand_users_device_types_required')
    }
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const submit = async () => {
    if (!shouldEnableCreateTechnician || !validate()) return false

    try {
      await createTechnician({
        variables: {
          input: {
            deviceTypes: values.deviceTypes,
            email: values.email.trim(),
            facilityIds: values.facilityIds,
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
          },
        },
        refetchQueries: [{ query: TechniciansDocument }],
      })
      showSuccess(t('settings:rebrand_users_create_technician_success'))
      return true
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          action: 'create_technician',
          feature: 'managed_accounts',
          surface: 'settings_users_access',
        },
      })
      showError(t('settings:rebrand_users_create_technician_error'))
      return false
    }
  }

  return {
    canCreate: shouldEnableCreateTechnician,
    errors,
    facilityOptions,
    isError: isFacilitiesError,
    isLoading: isFacilitiesLoading,
    isSaving: isCreating,
    setFacilitySearch,
    setField,
    submit,
    values,
  }
}
